export interface SeriesInfo {
  title: string;
  author: string;
  tagline: string;
  description: {
    short: string;
    medium: string;
    full: string;
  };
  setting: {
    location: string;
    population: number;
    description: string;
    atmosphere: string[];
    keyLocations: string[];
  };
  themes: string[];
  genre: {
    primary: string;
    secondary: string[];
    comparisons: string[];
  };
  targetAudience: {
    primary: string;
    demographics: {
      age: string;
      gender: string;
      education: string;
      readingHabits: string;
    };
  };
  marketingHooks: string[];
  socialMediaHooks: string[];
  status: {
    isComplete: boolean;
    plannedBooks: number;
    completedBooks: number;
  };
}

export const seriesInfo: SeriesInfo = {
  title: 'Memory Keeper Mysteries',
  author: 'M.K. Rivers',
  tagline: 'Some memories are too dangerous to forget',
  description: {
    short: 'In the fog-shrouded town of Cedar Falls, some memories are too dangerous to remember—and some secrets are too deadly to reveal. Dr. Sam Rivers specializes in helping people recover suppressed memories, but each recovered memory leads to another buried conspiracy that powerful people will kill to protect.',
    medium: `In the fog-shrouded town of Cedar Falls, Washington, some memories are too dangerous to remember—and some secrets are too deadly to reveal. Dr. Sam Rivers thought he could escape the ethical complexities of big-city psychology by opening a practice in this quiet Pacific Northwest community. Instead, he discovered that helping people recover suppressed memories is a deadly profession when those memories expose decades-old conspiracies.

Each book in the Memory Keeper Mysteries follows Dr. Rivers as he helps clients uncover traumatic memories they've buried, only to discover that their forgotten past threatens the carefully constructed lies that keep Cedar Falls' most powerful residents in control. From trusted priests to beloved community leaders, the people who seem most trustworthy often have the most to hide.

Perfect for fans of psychological thrillers who enjoy complex characters, atmospheric small-town settings, and mysteries that explore the dangerous intersection of memory, truth, and survival.`,
    full: `Welcome to Cedar Falls, Washington—population 8,000, where the mist never quite clears and some memories are too dangerous to remember.

Dr. Sam Rivers came to this quiet Pacific Northwest town seeking a simpler life, hoping to help people heal from everyday trauma. But in Cedar Falls, no trauma is ever simple, and no memory stays buried forever. As a psychologist specializing in memory recovery, Sam helps clients uncover suppressed memories of childhood experiences they've forgotten for good reason.

The problem? In a small town where everyone knows everyone else's business, those recovered memories inevitably expose deadly secrets that powerful people have spent decades hiding. From the beloved priest with a dark past to the trusted mayor with blood on his hands, the most respected members of the community often have the most to fear from the truth.

The Memory Keeper Mysteries series explores the dangerous world of recovered memories, where the line between forgotten trauma and deliberate cover-up becomes a matter of life and death. Each book follows Dr. Rivers and his growing team of allies—including detective Maya Chen, survivor-turned-advocate Claire Morrison, and forensic expert Dr. Elena Vasquez—as they navigate the complex ethics of memory recovery while staying one step ahead of killers who will do anything to keep their secrets buried.

Set against the atmospheric backdrop of misty forests and small-town secrets, this series examines how communities protect their darkest truths, the unreliable nature of memory, and the price of uncovering conspiracies that reach into the highest levels of local power. With each recovered memory, the stakes get higher, the conspiracies get deeper, and the danger gets more personal.

Perfect for readers who love psychological complexity, atmospheric settings, and mysteries where the real question isn't just "who did it" but "who's been covering it up—and why?"`
  },
  setting: {
    location: 'Cedar Falls, Washington',
    population: 8000,
    description: 'A fictional small town in the Pacific Northwest, nestled between evergreen forests and mist-covered mountains. Cedar Falls appears to be the perfect place to heal from trauma, but beneath its peaceful facade lie decades of buried secrets, covered-up crimes, and a web of complicity that reaches into the highest levels of local leadership.',
    atmosphere: [
      'Constant fog and rain',
      'Overcast skies that mirror psychological darkness',
      'Isolated location maintaining small-town dynamics',
      'Close-knit community where everyone knows everyone\'s business',
      'Historic buildings harboring dark histories'
    ],
    keyLocations: [
      'Dr. Sam Rivers\' Victorian office on Maple Street',
      'St. Andrew\'s Catholic Church',
      'Cedar Falls Police Station',
      'The Daily Grind coffee shop',
      'Murphy House (crime scene)',
      'Blackwood Mansion (abandoned)',
      'Cedar Falls Community Center',
      'Old mill site by the river',
      'Cedar Falls High School',
      'Medical Examiner\'s Office'
    ]
  },
  themes: [
    'Memory and Truth - The unreliable nature of memory vs. the power of suppressed trauma',
    'Community Complicity - How small towns protect their own, even when they\'re criminals',
    'Trust and Betrayal - Betrayal by trusted authority figures',
    'Professional Ethics vs. Moral Responsibility - Confidentiality vs. preventing harm',
    'Institutional Corruption - Abuse of authority and community trust',
    'Psychological Manipulation - The fine line between healing and harming',
    'Survivor Advocacy - Finding voice and power after trauma',
    'Small-town Secrets - The price of maintaining false peace'
  ],
  genre: {
    primary: 'Psychological Thrillers',
    secondary: [
      'Small-Town Mysteries',
      'Police Procedurals',
      'Literary Fiction'
    ],
    comparisons: [
      'Like Louise Penny\'s Inspector Gamache series, but with psychological manipulation instead of cozy murders',
      'Combines the psychological complexity of Tana French with the small-town atmosphere of Stephen King',
      'For fans of Gillian Flynn who want a series with the same psychological depth'
    ]
  },
  targetAudience: {
    primary: 'Adults 25-55 who enjoy psychological thrillers',
    demographics: {
      age: '25-65 years old',
      gender: '60% female, 40% male',
      education: 'College-educated',
      readingHabits: '2-4 books per month, prefer series'
    }
  },
  marketingHooks: [
    'What happens when recovering memories becomes a deadly profession?',
    'In this small town, the people you trust most are the ones you should fear',
    'Every recovered memory reveals another conspiracy',
    'Some secrets are worth killing for—some memories are worth dying for',
    'Memory is the ultimate unreliable witness'
  ],
  socialMediaHooks: [
    'Your therapist helps you forget trauma. Dr. Sam Rivers helps you remember it. #MemoryKeeperMysteries',
    'Small town. Big secrets. Deadly memories. #PacificNorthwestThriller',
    'Trust no one. Remember everything. Survive if you can. #PsychologicalThriller'
  ],
  status: {
    isComplete: false, // Set to true when all planned books are published
    plannedBooks: 8,   // Total books planned for the series (6-8 books)
    completedBooks: 2  // Currently published books (Fractured Recall + Blackwood Mansion)
  }
};

export const authorInfo = {
  name: 'M.K. Rivers',
  bio: {
    short: 'M.K. Rivers is the author of the Memory Keeper Mysteries series. When not writing psychological thrillers, they can be found exploring the misty forests of the Pacific Northwest that inspire their atmospheric settings.',
    medium: `M.K. Rivers writes psychological thrillers that explore the dangerous intersection of memory, truth, and survival. The Memory Keeper Mysteries series was born from a fascination with the unreliable nature of memory and the dark secrets that small communities will kill to protect.

Rivers draws inspiration from the atmospheric Pacific Northwest, where fog-shrouded forests and isolated communities provide the perfect backdrop for exploring how the past refuses to stay buried. When not writing, Rivers enjoys hiking misty trails, visiting small-town bookshops, and researching the psychology of memory recovery.

The debut novel, "Fractured Recall," introduces readers to Cedar Falls, Washington, where psychologist Dr. Sam Rivers discovers that helping people recover suppressed memories can be a deadly profession.`,
    full: `M.K. Rivers is an emerging voice in psychological thrillers, bringing a unique blend of authentic psychology and atmospheric storytelling to the genre. The Memory Keeper Mysteries series explores the dangerous world of recovered memories, where the line between forgotten trauma and deliberate cover-up becomes a matter of life and death.

Rivers' fascination with memory psychology began during graduate studies in cognitive psychology, leading to extensive research into memory recovery techniques, false memory syndrome, and the ethical complexities of therapeutic practice. This academic background provides the authentic psychological foundation that distinguishes the Memory Keeper Mysteries from traditional crime fiction.

The series is set in the atmospheric Pacific Northwest, where Rivers spent years exploring small communities nestled between misty forests and mountain ranges. These travels revealed the fascinating dynamics of isolated towns where everyone knows everyone's business—and the dangerous lengths people will go to protect community secrets.

"Fractured Recall," the series debut, introduces Dr. Sam Rivers, a psychologist who fled big-city FBI consulting work for the quiet town of Cedar Falls, only to discover that small-town trauma can be just as deadly as urban crime. The book explores themes of professional ethics, memory reliability, and the price of uncovering truths that powerful people will kill to keep buried.

Rivers currently lives in the Pacific Northwest, where the constant interplay of light and shadow through ancient forests continues to inspire the atmospheric settings that define the Memory Keeper Mysteries. When not writing, Rivers enjoys hiking misty trails, researching historical cold cases, and visiting the kind of small-town bookshops and coffee houses that populate Cedar Falls.

The Memory Keeper Mysteries series is planned as a 5-7 book arc, with each novel exploring different aspects of memory psychology while building the complex mythology of Cedar Falls and its inhabitants. Future books will delve deeper into generational trauma, cult psychology, technological memory manipulation, and the ongoing evolution of Dr. Sam Rivers and his team of allies.`
  },
  website: 'https://memorykeepermysteries.com',
  social: {
    twitter: '@MKRiversBooks',
    instagram: '@memorykeepermysteries',
    facebook: 'MKRiversAuthor',
    goodreads: '12345678-m-k-rivers'
  },
  newsletter: 'Sign up for updates on new releases, behind-the-scenes content, and exclusive Cedar Falls stories.',
  photo: '/author/mk-rivers-author-photo.jpg'
};